import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { SaveStatus } from '@design/models/save-status.model';
import { combineLatest, forkJoin, Observable, Subject, Subscription, timer } from 'rxjs';
import { takeUntil, retry, map, finalize, take } from 'rxjs/operators';
import { AdditionalToolsApi } from '@design/api/additional-tools-api';
import { QuotesDetailsApi } from '@design/api/quotes-details-api';
import { AuthService } from 'src/app/helpers/auth.service';
import { environment } from 'src/environments/environment';
import { Action, Store } from '@ngrx/store';
import { DesignState } from '@design/store/design.reducer';
import { fromAdditionalToolsResultsSelectors } from '@design/store/additional-tools';
import { fromMetaDataActions, fromMetaDataSelectors } from '@design/store/meta-data';
import { fromEdgeSelectors } from '@design/store/edge';
import { fromObstacleSelectors } from '@design/store/obstacle';
import { fromRulerSelectors } from '@design/store/ruler';
import { fromPanelArraySelectors } from '@design/store/panel-array/panel-array.selector';
import { fromPanelSelectors } from '@design/store/panel/panel.selector';
import { fromRoofSectionSelectors } from '@design/store/roof-section';
import { fromTreesSelectors } from '@design/store/trees';
import { fromDesignComponentSelectors } from '@design/store/design-component';
import { fromVertexSelectors } from '@design/store/vertex';
import { Panel } from '@design/models/panel.model';
import { fromFrameSelectors } from '@design/store/frame/frame.selector';
import { fromCustomImageSelectors } from '@design/store/custom-image';

//Factory function to create BatchAutoSaveService instances
export function createBatchAutoSaveService(
    http: HttpClient,
    authService: AuthService,
    quotesDetailsApi: QuotesDetailsApi,
    store: Store<DesignState>
): BatchAutoSaveService {
    return new BatchAutoSaveService(http, authService, quotesDetailsApi, store);
}

/// additional tool api
/// 1.saveQuote3DIsometricViewImage => quotes/house-view-image
/// 2.saveQuoteBackgroundImage => nact-backend/quotes/saveBackgroundImage
/// Quote details api
/// 3.updateQuoteHardware =>quote/hardware
/// 4.updateQuoteDesign =>quote/design
/// 5.saveDataToS3 => response api url
/// 6.getUploadLink => site/3d-object
/// 7.getPVWattsHourly => getPVWattsHourly

@Injectable({
    providedIn: 'root',
})
export class BatchAutoSaveService {
    private cancelInprogressRequests$: Subject<void> | null = null;
    private cancelTimer$: Subject<void> | null = null;
    private currentVersion = 0;
    private apiCalls: {[name: string]:Observable<any>} = {};
    private versionHistory: { v: number, status: 'success' | 'failed' }[] = [];
    private saveStatus: SaveStatus = SaveStatus.SAVED;
    private $batchSaveStatus: Subject<SaveStatus> | null = null;
    private tempApiCallsObservabled: Observable<any>[] | null = null;
    private readonly WAIT_TIME_TO_OVERRIDE = 10000; /// 10 seconds

    private design3dModel: Subject<Action> | null = null;
    private _design3dModel$: Observable<Action> | null = null;

    quoteDesignData: any = null;
    getUploadedLink$?: Subscription;
    private isDestroyed = false;

    constructor(
        private http: HttpClient,
        private authService: AuthService,
        private quotesDetailsApi: QuotesDetailsApi,
        private store: Store<DesignState>,
    ) {
        this.initializeSubjects();
    }

    private initializeSubjects(): void {
        if (this.isDestroyed) return;
        
        this.cancelInprogressRequests$ = new Subject<void>();
        this.cancelTimer$ = new Subject<void>();
        this.$batchSaveStatus = new Subject<SaveStatus>();
        this.design3dModel = new Subject<Action>();
        this._design3dModel$ = this.design3dModel.asObservable();
    }

    get design3dModel$(): Observable<Action> {
        if (!this._design3dModel$) {
            this.initializeSubjects();
        }
        return this._design3dModel$!;
    }
    private setBatchAutoSaveStatus(value: SaveStatus): void {
        if (this.isDestroyed || !this.$batchSaveStatus) return;
        
        this.saveStatus = value;
        this.$batchSaveStatus.next(value);
    }

    public batchAutoSaveStatus(): Observable<SaveStatus> {
        if (!this.$batchSaveStatus) {
            this.initializeSubjects();
        }
        return this.$batchSaveStatus!.asObservable();
    }

    /**
     * NOTES(dk): updateDesign is used to ensure data consistency between quote design data and
     * 3D model data.
     * 1. updateDesign is called wherever quote design data is updated.
     * 2. The update will then retrieve the 3D model from the three-js component using the design3dModel subject.
     * 3. It gets an upload link for S3 after receiving the 3D model data.
     * 4. It makes a save quote design call and saves 3D model data in parallel after receiving the upload link.
     */
    public updateDesign(designData: any, action: Action): void {
        if (this.isDestroyed) {
            console.warn('BatchAutoSaveService is disposed, cannot update design');
            return;
        }
        
        // Validate design data to prevent auto-save issues with empty/invalid data
        if (!designData) {
            console.warn('BatchAutoSaveService: designData is null or undefined, skipping auto-save');
            this.setBatchAutoSaveStatus(SaveStatus.SAVED);
            return;
        }
        
        this.store.select(fromAdditionalToolsResultsSelectors.sceneRestoreStatus).pipe(take(1)).subscribe(x => {
            if (x === SaveStatus.SAVED || x === SaveStatus.FAILED) {
                this.quoteDesignData = designData;
                this.get3dModelData(action);
            } else {
                console.info('auto save stopped from updateDesign as design not restored yet.');
            }
        })
    }

    public onReceiving3dModelData(design3dData: any): void {
        if (this.isDestroyed) return;
        
        // Validate 3D model data to prevent auto-save issues
        if (!design3dData || !this.quoteDesignData) {
            console.warn('BatchAutoSaveService: Invalid 3D model data or quote design data, marking as saved');
            this.setBatchAutoSaveStatus(SaveStatus.SAVED);
            this.reset();
            return;
        }
        
        // Clean up previous subscription
        if (this.getUploadedLink$) {
            this.getUploadedLink$.unsubscribe();
            this.getUploadedLink$ = undefined;
        }

        this.getUploadedLink$ = this.getUploadedLink()
            .subscribe({
                next: (res: any) => {
                    if (this.isDestroyed) return;
                    
                    const data = { ...res.data };
                    data['model'] = design3dData;
                    const apiCalls = [
                        this.quotesDetailsApi.updateQuoteDesign(this.quoteDesignData),
                        this.quotesDetailsApi.saveDataToS3(data),
                    ];
                    console.info('Scene data to be saved in S3 : ', data);
                    this.batchApiSave(apiCalls);
                    
                    // Clear references immediately after use
                    this.quoteDesignData = null;
                    data.model = null;
                    
                    // Reset service to prevent memory accumulation
                    this.reset();
                },
                error: (error) => {
                    console.error('Error in onReceiving3dModelData:', error);
                    this.setBatchAutoSaveStatus(SaveStatus.FAILED);
                    this.reset(); // Reset even on error
                }
            });
    }

    private get3dModelData(action: Action): void {
        if (this.isDestroyed || !this.design3dModel) return;
        
        this.design3dModel.next(action); /// this will call onReceiving3dModelData from three js component
    }

    private getUploadedLink(): Observable<any> {
        const qrn = { qrn: this.authService.getItem('qrn', 'localStorage') };
        return this.http.post(`${environment.s3baseurl}site/3d-object`, qrn);
    }

    private batchApiSave(apiCallsObservables: Observable<any>[]): void {
        if (this.isDestroyed) return;
        
        const isSaveBatchApiCallsInprogress = this.saveStatus === SaveStatus.INPROGRESS;
        if (isSaveBatchApiCallsInprogress) {
            this.tempApiCallsObservabled = apiCallsObservables;
            this.cancelTimer();
            
            if (!this.cancelTimer$) return;
            
            timer(this.WAIT_TIME_TO_OVERRIDE).pipe(
                takeUntil(this.cancelTimer$),
                map(() => {
                    if (this.isDestroyed || !this.tempApiCallsObservabled) return;
                    
                    this.makeBatchApiCalls(this.tempApiCallsObservabled);
                    this.tempApiCallsObservabled = null;
                }),
            ).subscribe();
            return;
        }

        this.makeBatchApiCalls(apiCallsObservables);
    }

    private makeBatchApiCalls(services: Observable<any>[] = []): void {
        if (this.isDestroyed || services.length === 0) return;

        const oldCancel = this.cancelInprogressRequests$;
        this.cancelInprogressRequests$ = new Subject();
        
        // Clean up old cancel subject
        if (oldCancel) {
            oldCancel.next();
            oldCancel.complete();
        }

        this.setBatchAutoSaveStatus(SaveStatus.INPROGRESS);

        forkJoin(services)
            .pipe(
                takeUntil(this.cancelInprogressRequests$),
                finalize(() => {
                    this.setBatchAutoSaveStatus(SaveStatus.CANCELLED);
                }),
                retry(1) //if any api fails. it will retry all the api thrice
            ).subscribe({
                next: (savedData) => {
                    this.setBatchAutoSaveStatus(SaveStatus.SAVED);
                    this.store.dispatch(fromMetaDataActions.markSceneStatus({ isNewQuote: false }));
                    this.saveVersionData({ v: this.currentVersion, status: 'success' }, savedData);
                    this.checkForTriggerApiCalls();
                },
                error: (error) => {
                    this.saveVersionData({ v: this.currentVersion, status: 'failed' }, error);
                    this.setBatchAutoSaveStatus(SaveStatus.FAILED);
                }
            })
    }

    private checkForTriggerApiCalls(): void {
        if (this.isDestroyed) return;
        
        const hasPendingBatchRequest = this.tempApiCallsObservabled;
        if (hasPendingBatchRequest) {
            this.cancelTimer();
            this.batchApiSave(this.tempApiCallsObservabled!);
            this.tempApiCallsObservabled = null;
        }
    }

    private cancelTimer(): void {
        if (this.cancelTimer$) {
            this.cancelTimer$.next();
            this.cancelTimer$.complete();
        }
        this.cancelTimer$ = new Subject();
    }

    // private cancelInprogressRequests(){
    //     this.cancelInprogressRequests$.next();
    //     this.cancelInprogressRequests$.complete();
    //     this.cancelInprogressRequests$ = new Subject();
    // }

    private saveVersionData(versionData: { v: number, status: 'success' | 'failed' }, data?: any): void {
        // Limit version history to prevent memory bloat
        if (this.versionHistory.length > 50) {
            this.versionHistory = this.versionHistory.slice(-25); // Keep only last 25 versions
        }
        
        this.versionHistory.push(versionData);
        console.log(versionData, data); /// Needs to be removed after testing
    }

    public createStoreJson(): Observable<any> {
        if (this.isDestroyed) {
            throw new Error('BatchAutoSaveService has been destroyed');
        }
        
        return combineLatest([
            this.store.select(fromTreesSelectors.allTrees),
            this.store.select(fromObstacleSelectors.allObstacles),
            this.store.select(fromVertexSelectors.allVertices),
            this.store.select(fromEdgeSelectors.allEdges),
            this.store.select(fromMetaDataSelectors.mapCameraInfo),
            this.store.select(fromPanelArraySelectors.allPanelArrays),
            this.store.select(fromPanelSelectors.allpanels),
            this.store.select(fromFrameSelectors.allFrames),
            this.store.select(fromRoofSectionSelectors.allRoofSections),
            this.store.select(fromMetaDataSelectors.getSelectedMap),
            this.store.select(fromCustomImageSelectors.selectCustomImage),
            this.store.select(fromRulerSelectors.selectAllRulers),
            this.store.select(fromDesignComponentSelectors.allDesignComponents),
        ]).pipe(
            map(([
                trees,
                obstacles,
                allVertices,
                allEdges,
                visibleMap,
                panelArrays,
                panels,
                frames,
                roofs,
                selectedMap,
                customImage,
                rulers,
                designComponents
            ]) => {
                // Type-safe array operations
                const treesArray = Array.isArray(trees) ? trees : [];
                const obstaclesArray = Array.isArray(obstacles) ? obstacles : [];
                const panelArraysArray = Array.isArray(panelArrays) ? panelArrays : [];
                const panelsArray = Array.isArray(panels) ? (panels) : [];
                
                const keepOuts = [...treesArray, ...obstaclesArray];

                const groups = panelArraysArray.map((group: any) => {
                    const panelsForGroup = panelsArray.filter((panel: Panel) => panel.association === group.roofId);
                    return {
                        id: group.id,
                        position: panelsForGroup
                    };
                });

                const updatePanelsInDesign: any = {
                    panel: {
                        groups
                    },
                    frames,
                    roofOutlines: roofs,
                    keepOuts,
                    vertices: allVertices,
                    edges: allEdges,
                    map: selectedMap !== 2 ? visibleMap : { selected: 2 },
                    isNewQuote: false,
                    // Custom image metadata for restoration (position, rotation, opacity, scale)
                    customImage: customImage ? {
                        position: customImage.position,
                        rotation: customImage.rotation,
                        opacity: customImage.opacity,
                        scale: customImage.scale,
                        calibration: customImage.calibration,
                        originalWidthPx: customImage.originalWidthPx,
                        originalHeightPx: customImage.originalHeightPx
                    } : null,
                    // Persist placement rulers so each measurement survives the
                    // round-trip through the quote API.
                    rulers: Array.isArray(rulers) ? rulers : [],
                    // Persist placed Storage/Inverter components (physical
                    // equipment locations) so they survive the round-trip and
                    // can feed PVCAD export.
                    designComponents: Array.isArray(designComponents) ? designComponents : []
                };
                console.info('auto save attempted');
                return updatePanelsInDesign;
            })
        );
    }

    /**
     * Reset the service to clear accumulated data and prevent memory leaks
     * Call this after onReceiving3dModelData to clear references
     */
    public reset(): void {
        if (this.isDestroyed) return;

        // Clear version history to prevent accumulation
        if (this.versionHistory.length > 10) {
            this.versionHistory = this.versionHistory.slice(-5); // Keep only last 5 versions
        }

        // Clear API calls cache
        this.apiCalls = {};

        // Reset quote design data
        this.quoteDesignData = null;

        // Clear temp API calls
        this.tempApiCallsObservabled = null;

        console.info('BatchAutoSaveService reset completed');
    }

    /**
     * Dispose of all resources and clean up memory
     */
    public dispose(): void {
        this.isDestroyed = true;

        // Cancel any ongoing subscriptions
        if (this.getUploadedLink$) {
            this.getUploadedLink$.unsubscribe();
            this.getUploadedLink$ = undefined;
        }

        // Clean up subjects
        if (this.cancelInprogressRequests$) {
            this.cancelInprogressRequests$.next();
            this.cancelInprogressRequests$.complete();
            this.cancelInprogressRequests$ = null;
        }

        if (this.cancelTimer$) {
            this.cancelTimer$.next();
            this.cancelTimer$.complete();
            this.cancelTimer$ = null;
        }

        if (this.$batchSaveStatus) {
            this.$batchSaveStatus.complete();
            this.$batchSaveStatus = null;
        }

        if (this.design3dModel) {
            this.design3dModel.complete();
            this.design3dModel = null;
        }

        // Clear all data
        this.quoteDesignData = null;
        this.tempApiCallsObservabled = null;
        this._design3dModel$ = null;
        this.apiCalls = {};
        this.versionHistory = [];
        
        console.info('BatchAutoSaveService disposed');
    }

    // private increaseCurrentVersion(isMinorVersion = false){
    //     if(isMinorVersion){
    //         this.currentVersion += 0.1
    //     }else{
    //         this.currentVersion++;
    //     }
    // }

}

